import { computed, InjectionToken, linkedSignal, signal, Signal, ValueEqualityFn } from '@angular/core';
import { isDefined } from '../../functions/is-defined.function';

export interface MultiSelectServiceOptions<T> {
  equals?: ValueEqualityFn<T>
  keepPreviousSelection?: undefined | 'equality' | 'index';
}

export class MultiSelectService<T> {

  private _selectedIndexes = linkedSignal<T[] | null | undefined, number[]>(
    {
      source: () => this.items(),
      computation: (items, previous ) => {
        if(!items || !items.length)
          return [];

        if(previous && this.options?.keepPreviousSelection === 'index') {
          return previous.value.filter(o => o <= (items.length - 1));
        }
        if (previous && this.options?.keepPreviousSelection === 'equality') {
          const previousItems = previous.source;
          const previousIndexes = previous.value;
          if(previousItems && previousIndexes.length > 0) {
            const previousSelectedItems = previousIndexes.map(index => previousItems[index])
              .filter(o => isDefined(o));
            return previousSelectedItems
              .map(previousItem => items.findIndex(o => this.options?.equals ? this.options.equals(o, previousItem) : o === previousItem))
              .filter(index => index > -1);
          }
        }
        return [];
      }
    });

  selectedIndexes = this._selectedIndexes.asReadonly();

  selected = computed(() => {
    const items = this.items() ?? [];
    const selectedIndexes = this.selectedIndexes();
    return selectedIndexes.map(index => items[index]);
  });

  result = computed(() => this.items() ?? []);

  constructor(protected items: Signal<T[] | null | undefined>, private options?: MultiSelectServiceOptions<T>) { }

  select = (item: T): void => {
    const items = this.items();
    if(!items || !items.length)
      return;

    const selectedIndexes = this.selectedIndexes();
    const index = items.findIndex(o => this.options?.equals ? this.options.equals(o, item) : o === item);
    if(index > -1)
      this._selectedIndexes.set([...selectedIndexes, index]);
  }

  unselect = (item: T): void => {
    const items = this.items();
    if(!items || !items.length)
      return;
    const selectedIndexes = this.selectedIndexes();
    const index = items.findIndex(o => this.options?.equals ? this.options.equals(o, item) : o === item);
    if(index > -1)
      this._selectedIndexes.set(selectedIndexes.filter(i => i !== index));
  }

  setSelected = (items: T[]): void  => {
    this.unselectAll();
    for (const item of items)
      this.select(item);
  }

  selectAll = (): void => {
    const items = this.items();
    if(!items || !items.length)
      return;

    const indexes = items.map((_, index) => index);
    this._selectedIndexes.set(indexes);
  }

  unselectAll = (): void => {
    this._selectedIndexes.set([]);
  }
}
